export { 
  SubscriptionTypes, 
  SubscriptionTypeLabels,
  SubscriptionTypeDescriptions,
  SubscriptionPolicies 
} from 'constants/subscriptionTypes';

export const FORM_STEPS = {
  BASIC_INFO: 0,
  CATEGORIES: 1,
  PRICING_TIERS: 2  
};

export const STEP_LABELS = [
  'Basic Information',
  'Categories & Features', 
  'Pricing Tiers'
];

export const ValuePackTierTypes = {
  LOW: 'Low',
  MID: 'Mid', 
  HIGH: 'High'
};

export const DiscountTypes = {
  FLAT: 'flat',
  PERCENTAGE: 'percentage'
};

export const AllowedDurations = [1, 3, 6, 9, 12, 18, 24];

export const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
  AUTOCOMPLETE: 'autocomplete',
  SWITCH: 'switch',
  DATE: 'date',
  DATETIME: 'datetime-local',
  NUMBER: 'number',
  EMAIL: 'email',
  URL: 'url',
  PASSWORD: 'password'
};

export const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'US Dollar', symbol: '$' },
  { value: 'EUR', label: 'Euro', symbol: '€' },
  { value: 'GBP', label: 'British Pound', symbol: '£' },
  { value: 'INR', label: 'Indian Rupee', symbol: '₹' },
  { value: 'CAD', label: 'Canadian Dollar', symbol: 'C$' },
  { value: 'AUD', label: 'Australian Dollar', symbol: 'A$' }
];

export const TARGET_INDUSTRIES = [
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'saas', label: 'SaaS' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'real-estate', label: 'Real Estate' },
  { value: 'finance', label: 'Finance' },
  { value: 'travel', label: 'Travel & Tourism' },
  { value: 'food-delivery', label: 'Food Delivery' },
];

export const BASIC_INFO_FIELDS = {
  name: {
    label: 'Value Pack Name *',
    placeholder: 'e.g., Web & Mobile Combo',
    helperText: 'This will be the main display name for your value pack',
    type: FIELD_TYPES.TEXT,
    maxLength: 100
  },
  subTitle: {
    label: 'Sub Title *',
    placeholder: 'e.g., Complete E-commerce Solution with Mobile App',
    helperText: 'A descriptive subtitle for your value pack (10-100 characters)',
    type: FIELD_TYPES.TEXT,
    maxLength: 100,
  },
  slug: {
    label: 'URL Slug *',
    placeholder: 'web-mobile-combo',
    helperText: 'Will be used in the URL for this value pack',
    type: FIELD_TYPES.TEXT,
    maxLength: 100
  },
  shortDescription: {
    label: 'Short Description *',
    placeholder: 'Brief description for display in listings',
    helperText: 'Brief description (max 255 characters) for display in listings',
    type: FIELD_TYPES.TEXTAREA,
    multiline: true,
    rows: 2,
    maxLength: 255
  },
  description: {
    label: 'Full Description *',
    placeholder: 'Detailed description of what this value pack includes...',
    helperText: 'Detailed description of the value pack and what it includes',
    type: FIELD_TYPES.TEXTAREA,
    multiline: true,
    rows: 4,
    maxLength: 2000
  },
  imageUrl: {
    label: 'Main Image URL *',
    placeholder: 'https://example.com/image.jpg',
    helperText: 'Upload or enter URL for the main image',
    type: FIELD_TYPES.URL
  },
  bannerImageUrl: {
    label: 'Banner Image URL',
    placeholder: 'https://example.com/banner.jpg',
    helperText: 'Upload or enter URL for the banner image',
    type: FIELD_TYPES.URL
  },
  publishedAt: {
    label: 'Publish Date',
    type: FIELD_TYPES.DATETIME,
    helperText: 'Schedule when this value pack should become available'
  },
  expiresAt: {
    label: 'Expiry Date (Optional)',
    type: FIELD_TYPES.DATETIME,
    helperText: 'Set when this value pack should expire'
  },
  tags: {
    label: 'Tags',
    placeholder: 'Add relevant tags...',
    helperText: 'Add relevant tags for categorization (1-10 tags)',
    type: FIELD_TYPES.MULTISELECT
  }
};

export const CATEGORIES_FIELDS = {
  categories: {
    label: 'Categories *',
    placeholder: 'Select categories to include...',
    helperText: 'Choose at least 2 categories to combine in this value pack (2-5 categories)',
    type: FIELD_TYPES.AUTOCOMPLETE,
    multiple: true
  },
  targetIndustries: {
    label: 'Target Industries',
    helperText: 'Select which industries this value pack is best suited for',
    type: FIELD_TYPES.MULTISELECT,
    options: TARGET_INDUSTRIES
  }
};

export const TIERS_FIELDS = {
  name: {
    label: 'Tier Name',
    placeholder: 'e.g., Starter Plan',
    helperText: 'Custom name for this tier',
    type: FIELD_TYPES.TEXT,
    maxLength: 100
  },
  description: {
    label: 'Tier Description',
    placeholder: 'Describe what this tier includes...',
    helperText: 'Brief description of tier features and benefits',
    type: FIELD_TYPES.TEXTAREA,
    multiline: true,
    rows: 3,
    maxLength: 500
  },
  price: {
    label: 'Base Price *',
    placeholder: '99.99',
    helperText: 'Base price before any discounts',
    type: FIELD_TYPES.NUMBER,
    min: 0
  },
  discountType: {
    label: 'Discount Type',
    options: [
      { value: DiscountTypes.FLAT, label: 'Flat Amount' },
      { value: DiscountTypes.PERCENTAGE, label: 'Percentage' }
    ],
    type: FIELD_TYPES.SELECT
  },
  flatDiscount: {
    label: 'Discount Amount',
    placeholder: '10.00',
    helperText: 'Flat discount amount to subtract from base price',
    type: FIELD_TYPES.NUMBER,
    min: 0
  },
  percentageDiscount: {
    label: 'Discount Percentage',
    placeholder: '15',
    helperText: 'Percentage discount to apply (0-100)',
    type: FIELD_TYPES.NUMBER,
    min: 0,
    max: 100
  },
  isPopular: {
    label: 'Mark as Popular',
    helperText: 'Highlight this tier as the most popular choice',
    type: FIELD_TYPES.SWITCH
  },
  duration: {
    label: 'Duration (Months)',
    options: AllowedDurations.map(d => ({
      value: d,
      label: d === 1 ? '1 Month' : d === 12 ? '1 Year' : `${d} Months`
    })),
    type: FIELD_TYPES.SELECT
  }
};

export const STATUS_OPTIONS = [
  {
    key: 'active',
    label: 'Active',
    description: 'Enable this value pack for public display',
    color: 'primary'
  },
  {
    key: 'featured',
    label: 'Featured',
    description: 'Mark as featured to highlight this value pack',
    color: 'secondary'
  }
];

export const DEFAULT_FORM_VALUES = {
  // Basic Info
  name: '',
  subTitle: '',
  description: '',
  shortDescription: '',
  slug: '',
  active: true,
  featured: false,

  imageUrl: '',
  bannerImageUrl: '',

  publishedAt: null,
  expiresAt: null,
  tags: [],

  // Categories
  categories: [],
  targetIndustries: [],
  filterValues: {},

  subscriptionType: '',      
  selectedDuration: null,      
  selectedCountry: '',  
  
  // Tiers (populated after creation)
  tiers: []
};

export const VALIDATION_MESSAGES = {
  REQUIRED: 'This field is required',
  MIN_LENGTH: (min) => `Must be at least ${min} characters`,
  MAX_LENGTH: (max) => `Must be less than ${max} characters`,
  INVALID_URL: 'Please enter a valid URL',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_SLUG: 'Only lowercase letters, numbers and hyphens allowed',
  FUTURE_DATE: 'Date must be in the future',
  MIN_SELECTION: (min) => `Please select at least ${min} item(s)`,
  MAX_SELECTION: (max) => `Maximum ${max} item(s) allowed`,
  DUPLICATE_TIER: 'Each tier type can only appear once per duration/country',
  EXPIRY_AFTER_PUBLISH: 'Expiry date must be after publish date',
  INVALID_PRICE: 'Price must be a valid number',
  INVALID_DISCOUNT: 'Discount cannot be greater than price',
  TIER_NAME_REQUIRED: 'Tier name is required when price is set',
  COUNTRY_REQUIRED: 'Country selection is required',
  DURATION_REQUIRED: 'Duration selection is required',
  SUBTITLE_MIN: `Sub title must be at least 10 characters`, 
  SUBTITLE_MAX: `Sub title must be less than 100 characters`,
};

export const FIELD_LIMITS = {
  NAME_MIN: 3,
  NAME_MAX: 100,
  SUBTITLE_MIN: 10, 
  SUBTITLE_MAX: 100,
  SHORT_DESCRIPTION_MAX: 255,
  DESCRIPTION_MIN: 50,
  DESCRIPTION_MAX: 2000,
  TIER_DESCRIPTION_MAX: 500,
  TAGS_MIN: 1,
  TAGS_MAX: 10,
  CATEGORIES_MIN: 2,
  CATEGORIES_MAX: 5,
  TIERS_MAX: 3,
  SLUG_MIN: 3,
  SLUG_MAX: 100
};

export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed'
};

export const API_ENDPOINTS = {
  VALUE_PACKS: '/api/value-packs',
  CATEGORIES: '/api/categories',
  FEATURES: '/api/features',
  UPLOAD: '/api/upload',
  COUNTRIES: '/api/countries',
  CURRENCIES: '/api/currencies'
};

export const STORAGE_KEYS = {
  VALUE_PACKS: 'valuePacks',
  CATEGORIES: 'categories',
  FORM_DRAFT: 'valuePackFormDraft',
  TIER_DRAFT: 'tierFormDraft'
};

export const UPLOAD_SETTINGS = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  IMAGE_QUALITY: 0.8,
  MAX_WIDTH: 1920,
  MAX_HEIGHT: 1080
};

export const CountryPricing = {
  price: 0,
  currency: 'USD',
  discount: 0,
  discountType: DiscountTypes.FLAT
};

export const Tier = {
  id: '',
  tierType: ValuePackTierTypes.LOW,
  name: '',
  description: '',
  isPopular: false,
  duration: 1,
  countryPricing: {},
  allowedDurations: [1],
  categoryFeatures: [],
  additionalFeatures: [],
  createdAt: '',
  updatedAt: ''
};

export const ValuePack = {
  id: '',
  name: '',
  subTitle: '',
  description: '',
  shortDescription: '',
  slug: '',
  active: true,
  featured: false,
  imageUrl: '',
  bannerImageUrl: '',
  categories: [],
  tags: [],
  targetIndustries: ['all'],
  publishedAt: null,
  expiresAt: null,
  tiers: [],
  createdAt: '',
  updatedAt: ''
};

export const getFieldConfig = (step, fieldName) => {
  switch (step) {
    case FORM_STEPS.BASIC_INFO:
      return BASIC_INFO_FIELDS[fieldName];
    case FORM_STEPS.CATEGORIES:
      return CATEGORIES_FIELDS[fieldName];
    case FORM_STEPS.TIERS:
      return TIERS_FIELDS[fieldName];
    default:
      return null;
  }
};

export const getStepFields = (step) => {
  switch (step) {
    case FORM_STEPS.BASIC_INFO:
      return Object.keys(BASIC_INFO_FIELDS);
    case FORM_STEPS.CATEGORIES:
      return Object.keys(CATEGORIES_FIELDS);
    case FORM_STEPS.TIERS:
      return Object.keys(TIERS_FIELDS);
    default:
      return [];
  }
};

export const isStepValid = (step, formData, isEditMode = false) => {
  const fields = getStepFields(step);
  
  switch (step) {
    case FORM_STEPS.BASIC_INFO:
      return fields.every(field => {
        if (['name', 'subTitle', 'slug', 'shortDescription', 'description'].includes(field)) {
          return formData[field] && formData[field].trim().length > 0;
        }
        
        // CREATE MODE: Images required, EDIT MODE: Images optional
        if (['imageUrl', 'bannerImageUrl'].includes(field)) {
          if (isEditMode) {
            return true; // Edit mode mein images optional
          }
          return formData[field] && formData[field].trim().length > 0;
        }
        
        return true;
      });
    
    case FORM_STEPS.CATEGORIES:
      // Target Industries is no longer in the form (industries are a custom filter)
      return formData.categories && formData.categories.length >= FIELD_LIMITS.CATEGORIES_MIN;
    
    case FORM_STEPS.TIERS:
      // For tiers, validation is optional during creation but required for edit mode
      return true;
    
    default:
      return false;
  }
};
